import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { MagneticButton } from "./MagneticButton";
import { AnimatedCounter } from "./AnimatedCounter";
import { WA_LINKS } from "../lib/utils";

const STATS = [
{ value: 25, suffix: "+", label: "Years of Expertise" },
{ value: 50, suffix: "+", label: "Professionals On-Ground" },
{ value: 7, suffix: "", label: "Major Cities Covered" },
{ value: 98, suffix: "%", label: "Client Satisfaction Rate" }];


export function Hero() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 0.5], ["0%", "20%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);

  return (
    <section ref={containerRef} className="relative flex min-h-screen flex-col overflow-hidden bg-navy-900" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <img
          src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=2000&q=85"
          alt="Luxury Indian villa exterior"
          className="absolute inset-0 h-full w-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-900/70 to-navy-950/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/40 to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: textOpacity }} className="container-luxury relative flex flex-1 flex-col justify-center pt-36 pb-24">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} className="mb-8 text-xs font-medium uppercase tracking-[0.22em] text-emerald-300">
          Premium NRI Property Management · India
        </motion.p>

        <div className="max-w-4xl">
          <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }} className="font-sans text-display-xl font-semibold leading-[0.97] tracking-tight text-white">
            Your Properties.
            <br />
            <span className="font-display italic font-normal text-gold-300">Our Responsibility.</span>
            <br />
            Your Peace of Mind.
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-xl text-balance text-lg leading-relaxed text-white/75 sm:text-xl">Sit back and collect returns while we handle every detail, tenants, rent collection, maintenance, legal support, and more. Full transparency, every step.

          </motion.p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.75, ease: [0.16, 1, 0.3, 1] }} className="mt-12 flex flex-wrap items-center gap-4">
          <MagneticButton href={WA_LINKS.healthCheck} external size="lg" variant="primary" icon={<MessageCircle size={18} />} data-testid="hero-health-check">Free Property Health Check</MagneticButton>
          <MagneticButton href={WA_LINKS.call} external size="lg" variant="ghost" className="border-white/30 text-white hover:border-white/60" icon={<ArrowRight size={18} />} data-testid="hero-book-call">Book a 15-min Call</MagneticButton>
        </motion.div>

        <motion.div initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-20 h-px w-full origin-left bg-gradient-to-r from-gold-400/50 via-gold-300/30 to-transparent" />

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }} className="mt-10 grid grid-cols-2 gap-y-8 gap-x-12 md:grid-cols-4">
          {STATS.map((stat, i) =>
          <motion.div key={stat.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 + i * 0.08, duration: 0.6 }} className="flex flex-col">
              <span className="font-sans text-3xl font-semibold tracking-tight text-white">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </span>
              <span className="mt-1 text-xs font-medium uppercase tracking-widest text-white/50">{stat.label}</span>
            </motion.div>
          )}
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>);

}